import React, { useEffect, useRef, useState } from 'react';

/** Rough center of Athens — used before coordinates exist */
const DEFAULT_CENTER = { lat: 37.9838, lng: 23.7275 };
const DEFAULT_ZOOM_NO_POSITION = 11;
const DEFAULT_ZOOM_WITH_POSITION = 16;

function parseLatLng(lat: unknown, lng: unknown): { lat: number; lng: number } | null {
	if (lat === undefined || lat === null || lat === '' || lng === undefined || lng === null || lng === '') {
		return null;
	}
	const la = typeof lat === 'number' ? lat : parseFloat(String(lat).replace(',', '.'));
	const ln = typeof lng === 'number' ? lng : parseFloat(String(lng).replace(',', '.'));
	if (Number.isNaN(la) || Number.isNaN(ln)) {
		return null;
	}
	return { lat: la, lng: ln };
}

type StoreLocationMapProps = {
	watch: (name: string) => unknown;
	onPinChange: (lat: number, lng: number) => void | Promise<void>;
};

/**
 * Embedded Google Map with a draggable marker. Notifies parent via onPinChange when the pin is released.
 */
const StoreLocationMap = ({ watch, onPinChange }: StoreLocationMapProps) => {
	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<google.maps.Map | null>(null);
	const markerRef = useRef<google.maps.Marker | null>(null);
	const dragListenerRef = useRef<google.maps.MapsEventListener | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);

	const onPinChangeRef = useRef(onPinChange);
	onPinChangeRef.current = onPinChange;

	const lat = watch('location.lat');
	const lng = watch('location.lng');

	const [mapsReady, setMapsReady] = useState(() => typeof window !== 'undefined' && !!window.google?.maps?.Map);

	useEffect(() => {
		if (mapsReady) {
			return;
		}
		const id = window.setInterval(() => {
			if (window.google?.maps?.Map) {
				setMapsReady(true);
				window.clearInterval(id);
			}
		}, 50);
		const timeout = window.setTimeout(() => window.clearInterval(id), 15000);
		return () => {
			window.clearInterval(id);
			window.clearTimeout(timeout);
		};
	}, [mapsReady]);

	useEffect(() => {
		if (!mapsReady) {
			return;
		}
		const el = mapContainerRef.current;
		if (!el || mapRef.current) {
			return;
		}

		const parsed = parseLatLng(watch('location.lat'), watch('location.lng'));
		const initial = parsed ?? DEFAULT_CENTER;
		const center = new window.google.maps.LatLng(initial.lat, initial.lng);
		const hasPosition = parsed !== null;

		const map = new window.google.maps.Map(el, {
			center,
			zoom: hasPosition ? DEFAULT_ZOOM_WITH_POSITION : DEFAULT_ZOOM_NO_POSITION,
			mapTypeControl: false,
			fullscreenControl: true,
			streetViewControl: false,
		});
		mapRef.current = map;

		const marker = new window.google.maps.Marker({
			position: center,
			map,
			draggable: true,
		});
		markerRef.current = marker;

		requestAnimationFrame(() => {
			window.google.maps.event.trigger(map, 'resize');
			const p = marker.getPosition();
			if (p) {
				map.setCenter(p);
			}
		});

		const onDragEnd = () => {
			const p = marker.getPosition();
			if (!p) {
				return;
			}
			void onPinChangeRef.current(p.lat(), p.lng());
		};
		dragListenerRef.current = window.google.maps.event.addListener(marker, 'dragend', onDragEnd);

		const ro = new ResizeObserver(() => {
			const m = mapRef.current;
			if (!m) {
				return;
			}
			window.google.maps.event.trigger(m, 'resize');
			const pos = markerRef.current?.getPosition();
			if (pos) {
				m.setCenter(pos);
			}
		});
		ro.observe(el);
		resizeObserverRef.current = ro;

		return () => {
			ro.disconnect();
			resizeObserverRef.current = null;
			if (dragListenerRef.current) {
				window.google.maps.event.removeListener(dragListenerRef.current);
				dragListenerRef.current = null;
			}
			marker.setMap(null);
			markerRef.current = null;
			mapRef.current = null;
		};
		// watch() only for initial center; pan is handled by the lat/lng sync effect
	}, [mapsReady]);

	useEffect(() => {
		const map = mapRef.current;
		const marker = markerRef.current;
		if (!map || !marker) {
			return;
		}

		const pos = parseLatLng(lat, lng);
		if (!pos) {
			return;
		}

		const ll = new window.google.maps.LatLng(pos.lat, pos.lng);
		marker.setPosition(ll);
		map.panTo(ll);
		map.setZoom(DEFAULT_ZOOM_WITH_POSITION);
	}, [lat, lng]);

	return (
		<div className="store-location-map w-100 rounded overflow-hidden border generic-border" style={{ height: 240 }}>
			<div ref={mapContainerRef} className="w-100" style={{ height: 240 }} role="presentation" aria-hidden />
		</div>
	);
};

export default StoreLocationMap;
